/**
 * Shared constants for canvas v2 node sizing.
 *
 * All image/video preview nodes use these maps to ensure consistent
 * card widths based on aspect ratio.
 */

/** CSS aspect-ratio values keyed by display ratio string. */
export const ASPECT_RATIO_MAP: Record<string, string> = {
  '16:9': '16/9',
  '9:16': '9/16',
  '3:4': '3/4',
  '4:3': '4/3',
  '1:1': '1/1',
  '3:2': '3/2',
  '2:3': '2/3',
  '2:1': '2/1',
  '5:4': '5/4',
  '4:5': '4/5',
  '21:9': '21/9',
  '9:21': '9/21',
  // Provider-chosen shape: a square placeholder until the asset's real
  // dimensions are known.
  Auto: '1/1',
}

/** Node card widths (px) keyed by aspect ratio.
 *  Must stay in sync with schemas/nodes/image_generator.json layout. */
export const ASPECT_WIDTH_MAP: Record<string, number> = {
  '16:9': 315,
  '9:16': 177,
  '3:4': 236,
  '4:3': 315,
  '1:1': 240,
  '3:2': 315,
  '2:3': 210,
  '2:1': 315,
  '5:4': 315,
  '4:5': 252,
  '21:9': 315,
  '9:21': 135,
  Auto: 240,
}

/** Standardised outer corner radius for every canvas node card — single source
 *  of truth (RFC: standardize-canvas-node-corner-radius). Keep the class and the
 *  px value in sync: `rounded-lg` = 10px per tailwind.config.js (theme.extend
 *  .borderRadius.lg, "use lg 10px max for premium feel").
 *
 *  - Class-based cards:  cn(NODE_CARD_RADIUS_CLASS, …)
 *  - Inline-style cards: style={{ borderRadius: NODE_CARD_RADIUS_PX }}
 *
 *  NODE_CARD_RADIUS_CLASS/NODE_CARD_INNER_RADIUS_CLASS must stay full string
 *  literals (never interpolated) so Tailwind's JIT content scanner emits them. */
export const NODE_CARD_RADIUS_CLASS = 'rounded-lg'
export const NODE_CARD_RADIUS_PX = 10
/** Inner preview-image radius for designer cards, concentric with the 10px outer
 *  under the 6px (p-1.5) inset: 10 − 6 = 4px = `rounded-sm`. */
export const NODE_CARD_INNER_RADIUS_CLASS = 'rounded-sm'

/** Intrinsic footer height used while content-visibility skips card content.
 *  CANVAS_NODE_QUICK_INFO_INTRINSIC_HEIGHT must fit the config column's content
 *  ("Config" label + 4px gap + config pill + 4px gap + 24px reference-thumbnail
 *  row) plus the >=10px (py-2.5) top/bottom padding without clipping. It is also
 *  the single source for the rendered QuickInfoBar height (see QuickInfoBar.tsx)
 *  so the two can never desync. */
export const CANVAS_NODE_QUICK_INFO_INTRINSIC_HEIGHT = 80

/** Numeric ratio values for snapping uploaded images to the closest bucket.
 *  Derived from ASPECT_WIDTH_MAP (pinned to the schema AspectRatio enum) so it
 *  matches the server's upload buckets in routes/assets.py. `Auto` means "the
 *  provider picks" and is not a shape, so it is never a snap target. */
const ASPECT_RATIOS: Array<{ label: string; ratio: number }> = Object.keys(ASPECT_WIDTH_MAP)
  .filter((label) => label !== 'Auto')
  .map((label) => ({ label, ratio: parseAspectRatioKey(label) }))

export interface SnappedDisplayDimensions {
  width: number
  height: number
  ratioKey: string
  aspectRatio: string
}

function parseAspectRatioKey(ratioKey: string): number {
  const [w, h] = ratioKey.split(':').map((part) => Number(part))
  if (!Number.isFinite(w) || !Number.isFinite(h) || h === 0) return 16 / 9
  return w / h
}

function parseCssAspectRatio(aspectRatio: string | undefined): number {
  const [w, h] = (aspectRatio ?? '').split(/[:/]/).map((part) => Number(part))
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return 16 / 9
  return w / h
}

/** True for a taller-than-wide CSS aspect ratio ("9/16", "3:4"). Portrait video
 *  previews stack their top-right overlay controls down the edge, because a row
 *  of buttons does not fit their width. */
export function isPortraitAspectRatio(aspectRatio: string): boolean {
  return parseCssAspectRatio(aspectRatio) < 1
}

export function getPreviewIntrinsicHeight(width: number, aspectRatio: string | undefined): number {
  const safeWidth = Number.isFinite(width) && width > 0 ? width : ASPECT_WIDTH_MAP['16:9']
  return Math.round(safeWidth / parseCssAspectRatio(aspectRatio))
}

export type CanvasNodeCardIntrinsicStyle = {
  '--canvas-node-card-intrinsic-width': string
  '--canvas-node-card-intrinsic-height': string
}

export function getCanvasNodeCardIntrinsicStyle(
  width: number,
  height: number,
): CanvasNodeCardIntrinsicStyle {
  const safeWidth = Number.isFinite(width) && width > 0 ? Math.round(width) : ASPECT_WIDTH_MAP['16:9']
  const safeHeight = Number.isFinite(height) && height > 0 ? Math.round(height) : 240
  return {
    '--canvas-node-card-intrinsic-width': `${safeWidth}px`,
    '--canvas-node-card-intrinsic-height': `${safeHeight}px`,
  }
}

/**
 * Snap natural media dimensions to the node display buckets used by the canvas.
 * The returned dimensions are authoritative for a selected, loaded asset.
 */
export function snapNaturalDimensionsToDisplayDimensions(
  naturalW: number,
  naturalH: number,
): SnappedDisplayDimensions {
  if (naturalW <= 0 || naturalH <= 0) {
    return {
      width: ASPECT_WIDTH_MAP['16:9'],
      height: Math.round(ASPECT_WIDTH_MAP['16:9'] / (16 / 9)),
      ratioKey: '16:9',
      aspectRatio: ASPECT_RATIO_MAP['16:9'],
    }
  }

  const mediaRatio = naturalW / naturalH
  let closest = ASPECT_RATIOS[0]
  let minDelta = Math.abs(mediaRatio - closest.ratio)
  for (let i = 1; i < ASPECT_RATIOS.length; i++) {
    const delta = Math.abs(mediaRatio - ASPECT_RATIOS[i].ratio)
    if (delta < minDelta) {
      minDelta = delta
      closest = ASPECT_RATIOS[i]
    }
  }

  const width = ASPECT_WIDTH_MAP[closest.label]
  return {
    width,
    height: Math.round(width / parseAspectRatioKey(closest.label)),
    ratioKey: closest.label,
    aspectRatio: ASPECT_RATIO_MAP[closest.label],
  }
}

/**
 * Given an image's natural dimensions, return the ASPECT_WIDTH_MAP width
 * for the closest matching aspect ratio bucket.
 */
export function snapToClosestAspectWidth(naturalW: number, naturalH: number): number {
  return snapNaturalDimensionsToDisplayDimensions(naturalW, naturalH).width
}
